import { remuxWebmToOgg } from './webmOpusToOgg';

const writeString = (view, offset, string) => {
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

const bufferToWav = async (buffer, numChannels, sampleRate) => {
  const length = buffer.length * numChannels * 2;
  const wav = new ArrayBuffer(44 + length);
  const view = new DataView(wav);

  // WAV Header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + length, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, length, true);

  // WAV Data
  const offset = 44;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < buffer.length; i++) {
    // eslint-disable-next-line no-plusplus
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(
        -1,
        Math.min(1, buffer.getChannelData(channel)[i])
      );
      view.setInt16(
        offset + (i * numChannels + channel) * 2,
        sample * 0x7fff,
        true
      );
    }
  }

  return new Blob([wav], { type: 'audio/wav' });
};

const decodeAudioData = async (audioBlob, sampleRate) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)(
    sampleRate ? { sampleRate } : undefined
  );
  try {
    const arrayBuffer = await audioBlob.arrayBuffer();
    return await audioContext.decodeAudioData(arrayBuffer);
  } finally {
    await audioContext.close();
  }
};

export const convertToWav = async audioBlob => {
  const audioBuffer = await decodeAudioData(audioBlob);
  return bufferToWav(
    audioBuffer,
    audioBuffer.numberOfChannels,
    audioBuffer.sampleRate
  );
};

/**
 * Encodes audio samples to MP3 format.
 * @param {number} sampleRate - Sample rate in Hz.
 * @param {Int16Array[]} samples - Separate sample buffers for each audio channel.
 * @param {number} bitrate - MP3 bitrate (default: 128)
 * @returns {Promise<Blob>} - The MP3 encoded audio as a Blob.
 */
export const encodeToMP3 = async (sampleRate, samples, bitrate = 128) => {
  const outputBuffer = [];
  const lamejs = (await import('@breezystack/lamejs')).default;
  const encoder = new lamejs.Mp3Encoder(samples.length, sampleRate, bitrate);
  const maxSamplesPerFrame = 1152;

  for (
    let offset = 0;
    offset < samples[0].length;
    offset += maxSamplesPerFrame
  ) {
    const mp3Buffer = encoder.encodeBuffer(
      ...samples.map(channel =>
        channel.subarray(offset, offset + maxSamplesPerFrame)
      )
    );

    if (mp3Buffer.length > 0) {
      outputBuffer.push(new Int8Array(mp3Buffer));
    }
    if (offset % (maxSamplesPerFrame * 32) === 0) {
      // Let the composer respond while encoding an attached recording.
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => {
        setTimeout(resolve, 0);
      });
    }
  }

  const remainingData = encoder.flush();
  if (remainingData.length > 0) {
    outputBuffer.push(new Int8Array(remainingData));
  }

  return new Blob(outputBuffer, { type: 'audio/mp3' });
};

/**
 * Converts an audio Blob to an MP3 format Blob.
 * @param {Blob} audioBlob - The audio data as a Blob.
 * @param {number} bitrate - MP3 bitrate (default: 128)
 * @returns {Promise<Blob>} - A Blob containing the MP3 encoded audio.
 */
export const convertToMp3 = async (
  audioBlob,
  bitrate = 128,
  sampleRate = undefined
) => {
  try {
    const audioBuffer = await decodeAudioData(audioBlob, sampleRate);
    if (![1, 2].includes(audioBuffer.numberOfChannels)) {
      throw new Error('MP3 supports mono or stereo audio.');
    }
    const samples = Array.from(
      { length: audioBuffer.numberOfChannels },
      (_, channel) =>
        Int16Array.from(audioBuffer.getChannelData(channel), value => {
          const sample = Math.max(-1, Math.min(1, value));
          return sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        })
    );
    return encodeToMP3(audioBuffer.sampleRate, samples, bitrate);
  } catch (error) {
    throw new Error('Conversion to MP3 failed.');
  }
};

export const convertSelectedAudioToMp3 = async audioBlob => {
  const audio = new Audio();
  const url = URL.createObjectURL(audioBlob);
  const sampleRate = 44100;
  let timer;
  try {
    const duration = await new Promise((resolve, reject) => {
      audio.onloadedmetadata = () => resolve(audio.duration);
      audio.onerror = () => reject(new Error('Cannot read audio metadata.'));
      timer = setTimeout(
        () => reject(new Error('Audio metadata timed out.')),
        15000
      );
      audio.preload = 'metadata';
      audio.src = url;
    });
    if (!Number.isFinite(duration) || duration <= 0) {
      throw new Error('Cannot determine audio duration.');
    }
    // ponytail: estimate at most 256 MiB of stereo PCM (float + int16).
    // Longer recordings need a streaming server converter; keep their original.
    if (duration * sampleRate * 2 * 6 > 256 * 1024 * 1024) {
      throw new RangeError(
        'Audio exceeds the browser conversion memory budget.'
      );
    }
    return await convertToMp3(audioBlob, 128, sampleRate);
  } finally {
    clearTimeout(timer);
    audio.onloadedmetadata = null;
    audio.onerror = null;
    audio.removeAttribute('src');
    audio.load();
    URL.revokeObjectURL(url);
  }
};

export const convertAudio = async (inputBlob, outputFormat, bitrate = 128) => {
  let audio;
  if (outputFormat === 'audio/wav') {
    audio = await convertToWav(inputBlob);
  } else if (outputFormat === 'audio/mp3') {
    audio = await convertToMp3(inputBlob, bitrate);
  } else if (outputFormat === 'audio/ogg') {
    const inputType = inputBlob.type.split(';')[0].trim();
    if (inputType === 'audio/webm' || inputType === 'video/webm') {
      audio = await remuxWebmToOgg(inputBlob);
    } else if (inputType === 'audio/ogg') {
      audio = inputBlob;
    } else {
      // Browsers that record neither WebM nor OGG (e.g. Safari records
      // audio/mp4) cannot produce OGG/Opus. Fall back to MP3 so the recording
      // still sends as a regular audio message instead of failing. The caller
      // keys the voice-note flag off the returned blob type, so an MP3 result
      // is never mislabeled as an OGG/Opus voice note.
      audio = await convertToMp3(inputBlob, bitrate);
    }
  } else {
    throw new Error('Unsupported output format');
  }
  return audio;
};
